import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { getCategoryDisplayName, IssueScopeKind } from '../../../../shared/issuesV2';
import LabeledCheckbox from '../../generic/Forms/LabeledCheckbox';
import { LabeledToggle } from '../../generic/Forms/LabeledToggle';
import Dropdown from '../../settings/shared/SettingsPageDropdown';
import type { Category } from '../../../../shared/issuesV2';
import type { SelectOption } from '../../../types';
import type { GenericIssueSettingsProps } from '../../projects/settingsv2/policies/types';

import Section from './SettingsPageSection';
import SnippetScanningSettingsSection from './SnippetScanningSettingsSection';
import VendoredDependencyScanningSettingsSection from './VendoredDependencyScanningSettingsSection';

import styles from './Settings.module.less';

type Props = {
  category: Category;
  scopeType: IssueScopeKind.Global | IssueScopeKind.Project;
  isSnippetScanningEnabled?: boolean;
  onToggleSnippetScanning?: (isChecked: boolean) => void;
  isVendoredScanningEnabled?: boolean;
  onToggleVendoredScanning?: (isChecked: boolean) => void;
} & GenericIssueSettingsProps;

/**
 * SettingsPageIssuesSection is a SettingsPageSection.Right component
 * for each of the issue category sections.
 */
const SettingsPageIssuesSection: VoidFunctionComponent<Props> = ({
  scopeType,
  category,
  isScanningEnabled,
  onToggleIsScanningEnabled,
  policyOptions,
  selectedPolicyOption,
  onSelectedPolicyChange,
  isCICheckEnabled,
  onToggleIsCICheckEnabled,
  filterOptions,
  selectedFilterOption,
  onSelectedFilterChange,
  canEditPolicy,
  canEditFilters,
  isSnippetScanningEnabled,
  onToggleSnippetScanning,
  isVendoredScanningEnabled,
  onToggleVendoredScanning,
}) => {
  const displayCategory = getCategoryDisplayName(category);
  const scopeProjectsText = scopeType === IssueScopeKind.Global ? 'all new projects' : 'this project';
  return (
    <>
      <LabeledToggle
        label="Scan for Issues"
        isChecked={isScanningEnabled}
        onChange={(isChecked) => onToggleIsScanningEnabled(isChecked)}
        isDisabled={!canEditPolicy}
      />
      <Section.Paragraph>
        This will turn on {displayCategory} issues for {scopeProjectsText}.
      </Section.Paragraph>
      <Section.Indented isDisabled={!isScanningEnabled}>
        <div
          className={cx(styles.issueSection, {
            [styles.issueSectionDisabled]: !isScanningEnabled,
          })}
        >
          <Dropdown
            id={`settings-project-issues-${category}-policy`}
            label={`Default ${displayCategory} policy`}
            options={policyOptions}
            value={selectedPolicyOption}
            onChange={(option: SelectOption<string | number | null> | null) => {
              //  null is a valid value, it's how users can unset this.
              if (option?.value === null || option?.value) {
                onSelectedPolicyChange(option.value as number | null);
              }
            }}
            isDisabled={!isScanningEnabled || !canEditPolicy}
          />
          <Section.Paragraph>This policy will be applied by default to {scopeProjectsText}.</Section.Paragraph>
          <LabeledCheckbox
            label={`Fail CI/CD checks if ${displayCategory} issues matching this filter exist.`}
            isChecked={isCICheckEnabled}
            onChange={(_event, isChecked: boolean) => onToggleIsCICheckEnabled(isChecked)}
            isDisabled={!isScanningEnabled}
            isRippleDisabled
          />
        </div>
        <div
          className={cx(styles.issueSection, {
            [styles.issueSectionDisabled]: !isScanningEnabled || !isCICheckEnabled,
          })}
        >
          <Dropdown
            id={`settings-project-issues-${category}-filter`}
            label={`CI/CD ${displayCategory} filter`}
            options={filterOptions}
            value={selectedFilterOption}
            onChange={(option: SelectOption<string | number | null> | null) => {
              //  null is a valid value, it's how users can unset this.
              if (option?.value === null || option?.value) {
                onSelectedFilterChange(option.value as number | null);
              }
            }}
            isDisabled={!isScanningEnabled || !isCICheckEnabled || !canEditFilters}
          />
        </div>
        {onToggleSnippetScanning && (
          <div className={cx(styles.issueSection, { [styles.issueSectionDisabled]: !isScanningEnabled })}>
            <SnippetScanningSettingsSection
              isSnippetScanningEnabled={isSnippetScanningEnabled ?? false}
              onToggleSnippetScanning={onToggleSnippetScanning}
              isScanningEnabled={isScanningEnabled}
              canEditPolicy={canEditPolicy}
            />
          </div>
        )}
        {onToggleVendoredScanning && (
          <div className={cx(styles.issueSection, { [styles.issueSectionDisabled]: !isScanningEnabled })}>
            <VendoredDependencyScanningSettingsSection
              isVendoredScanningEnabled={isVendoredScanningEnabled ?? false}
              onToggleVendoredScanning={onToggleVendoredScanning}
              isScanningEnabled={isScanningEnabled}
              canEditPolicy={canEditPolicy}
            />
          </div>
        )}
      </Section.Indented>
    </>
  );
};

export default SettingsPageIssuesSection;
